
import { useState, useEffect } from 'react';
import { useSubscription } from './useSubscription';

interface TrialReminderState {
  showPopup: boolean;
  showBanner: boolean;
  daysUntilReminder: number;
}

export function useTrialReminder() {
  const { subscription, isInTrial, daysLeftInTrial } = useSubscription();
  const [reminderState, setReminderState] = useState<TrialReminderState>({
    showPopup: false,
    showBanner: false,
    daysUntilReminder: 0
  });

  useEffect(() => {
    if (!isInTrial || !subscription) {
      setReminderState({
        showPopup: false,
        showBanner: false,
        daysUntilReminder: 0
      });
      return;
    }

    const lastReminderDate = localStorage.getItem('lastTrialReminderDate');
    const today = new Date().toDateString();
    
    // Show banner for all trial users
    const showBanner = daysLeftInTrial > 0;
    
    // Show popup every 3 days, or if never shown before
    let showPopup = false;
    if (!lastReminderDate) {
      showPopup = true;
    } else {
      const lastReminder = new Date(lastReminderDate);
      const daysSinceLastReminder = Math.floor((new Date().getTime() - lastReminder.getTime()) / (1000 * 60 * 60 * 24));
      showPopup = daysSinceLastReminder >= 3;
    }

    // Don't show popup if user just signed up today
    if (subscription.trialEndsAt) {
      const trialStartDate = new Date(subscription.trialEndsAt);
      trialStartDate.setDate(trialStartDate.getDate() - 14);
      const daysSinceSignup = Math.floor((new Date().getTime() - trialStartDate.getTime()) / (1000 * 60 * 60 * 24));
      if (daysSinceSignup < 1) {
        showPopup = false;
      }
    }

    setReminderState({
      showPopup: showPopup && daysLeftInTrial > 0,
      showBanner,
      daysUntilReminder: 3 - (lastReminderDate ? Math.floor((new Date().getTime() - new Date(lastReminderDate).getTime()) / (1000 * 60 * 60 * 24)) : 3)
    });
  }, [isInTrial, daysLeftInTrial, subscription?.trialEndsAt]);

  const dismissPopup = () => {
    localStorage.setItem('lastTrialReminderDate', new Date().toDateString());
    setReminderState(prev => ({ ...prev, showPopup: false }));
  };

  const dismissBanner = () => {
    setReminderState(prev => ({ ...prev, showBanner: false }));
  };

  return {
    ...reminderState,
    dismissPopup,
    dismissBanner,
    daysLeftInTrial
  };
}
